import React from 'react';
import { Pill } from '../ui';
import { DashboardRFQ } from '../../hooks/dashboardMappers';

/**
 * Display vocabulary shared by the RFQ list's card and table views — one
 * definition of the state tag, the honest n/9 progress bar and the date
 * formats, so the two views can never disagree about a row.
 */

/** Same short stage labels as the dashboard table — one vocabulary. */
export const STAGE_TAG_LABEL: Record<number, string> = {
    1: 'Upload BOM', 2: 'Criteria', 3: 'Discovery', 4: 'Outreach', 5: 'Quotes review',
};

const RAMP = ['bg-pipeline-1', 'bg-pipeline-2', 'bg-pipeline-3', 'bg-pipeline-4', 'bg-pipeline-5'];
/** 5 live stages + 4 locked — in-flight progress never claims the locked ones. */
export const TOTAL_STEPS = 9;

export const timeAgo = (iso: string): string => {
    const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
};

export const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * One tag telling the row's whole state: terminal statuses win (Awarded /
 * Cancelled), otherwise the in-flight stage tag in the dashboard's styling.
 */
export const RfqStateTag: React.FC<{ row: DashboardRFQ }> = ({ row }) => {
    if (row.awarded) return <Pill status="success">Awarded</Pill>;
    if (row.status === 'CANCELLED') return <Pill status="neutral">Cancelled</Pill>;
    const cls = row.stageStep >= 4
        ? 'bg-action-bg border-action-border text-action-deep'
        : 'bg-neutral-bg border-neutral-border text-neutral-ink';
    return (
        <span className={`whitespace-nowrap rounded-[6px] border px-s3 py-[3px] text-[11.5px] font-semibold ${cls}`}>
            {STAGE_TAG_LABEL[row.stageStep]}
        </span>
    );
};

/** Honest n/9 progress — completed-through step over the full 9-stage journey. */
export const RfqProgress: React.FC<{ row: DashboardRFQ }> = ({ row }) => (
    <span className="flex w-full items-center gap-[7px]">
        <span className="h-[4px] flex-1 overflow-hidden rounded-round bg-line-divider-soft">
            <span
                className={`block h-full ${RAMP[row.stageStep - 1]}`}
                style={{ width: `${(row.stageStep / TOTAL_STEPS) * 100}%` }}
            />
        </span>
        <span className="font-mono text-[11px] text-ink-muted">{row.stageStep}/{TOTAL_STEPS}</span>
    </span>
);

/** Bordered segmented control — reads as a filter, not clickable text. */
export function Segmented<T extends string>({ value, options, onChange, label }: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
    label: string;
}): React.ReactElement {
    return (
        <span role="group" aria-label={label} className="inline-flex overflow-hidden rounded-control border border-line">
            {options.map((opt, i) => (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    aria-pressed={value === opt.value}
                    className={`cursor-pointer whitespace-nowrap px-[10px] py-[4px] text-[12px] transition-colors ${i > 0 ? 'border-l border-line' : ''} ${
                        value === opt.value
                            ? 'bg-action-bg font-semibold text-action'
                            : 'bg-surface font-medium text-neutral-ink hover:bg-neutral-bg'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </span>
    );
}
